import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Company } from '../types';
import { companiesApi } from '../services/api';
import { HelpText, useToast } from '@vmcom/ui';

const SERVICE_LABELS: Record<string, string> = {
  products: 'Категории / ветки товаров',
  'products-units': 'Единицы товаров',
  'products-seo': 'SEO Products',
  'products-faq': 'ЧАВО товаров',
  brands: 'Бренды',
  blog: 'Блог',
  indexing: 'Индексация (Яндекс)',
};

type FormState = {
  name: string;
  main_site_host: string;
  assortment_mode: Company['assortment_mode'];
  enabled_services: string[];
};

const emptyForm = (): FormState => ({
  name: '',
  main_site_host: '',
  assortment_mode: 'both',
  enabled_services: Object.keys(SERVICE_LABELS),
});

interface CompanyManagerProps {
  onActiveChange?: (company: Company) => void;
}

const CompanyManager: React.FC<CompanyManagerProps> = ({ onActiveChange }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<Company[]>([]);
  const [editing, setEditing] = useState<Company | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [deleteTarget, setDeleteTarget] = useState<Company | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await companiesApi.list();
      setRows(data);
      const active = data.find((c) => c.is_active);
      if (active) onActiveChange?.(active);
    } catch {
      toast('Не удалось загрузить компании', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing({ id: '', name: '', assortment_mode: 'both', enabled_services: Object.keys(SERVICE_LABELS), is_active: false });
    setForm(emptyForm());
  };

  const openEdit = (r: Company) => {
    setEditing(r);
    setForm({
      name: r.name,
      main_site_host: r.main_site_host || '',
      assortment_mode: r.assortment_mode,
      enabled_services: r.enabled_services?.length ? [...r.enabled_services] : Object.keys(SERVICE_LABELS),
    });
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast('Укажите название компании', 'warning');
      return;
    }
    setLoading(true);
    try {
      if (editing?.id) {
        await companiesApi.update(editing.id, form);
        toast('Сохранено', 'success');
      } else {
        await companiesApi.create(form);
        toast('Компания добавлена', 'success');
      }
      setEditing(null);
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      toast(err?.response?.data?.detail || err?.message || 'Ошибка сохранения', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (r: Company) => {
    try {
      await companiesApi.activate(r.id);
      toast(`Активная компания: ${r.name}`, 'success');
      await load();
    } catch {
      toast('Не удалось переключить компанию', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await companiesApi.remove(deleteTarget.id);
      toast('Удалено', 'success');
      setDeleteTarget(null);
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      toast(err?.response?.data?.detail || 'Не удалось удалить', 'error');
    }
  };

  const toggleService = (key: string) => {
    setForm((f) => ({
      ...f,
      enabled_services: f.enabled_services.includes(key)
        ? f.enabled_services.filter((s) => s !== key)
        : [...f.enabled_services, key],
    }));
  };

  const modeLabel = (v: string) => ({ categories: 'Категории', branches: 'Ветки', both: 'Оба' }[v] || v);

  return (
    <Box>
      <HelpText>
        Активная компания определяет режим ассортимента и включённые сервисы. Переключайте организацию в шапке или здесь.
      </HelpText>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Добавить
        </Button>
        <Button variant="outlined" onClick={load} disabled={loading}>
          Обновить
        </Button>
      </Stack>
      {loading && !rows.length ? (
        <CircularProgress size={28} />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Компания</TableCell>
              <TableCell>Ассортимент</TableCell>
              <TableCell>Сервисы</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.is_active ? <CheckCircleIcon color="success" fontSize="small" /> : null}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell>{modeLabel(r.assortment_mode)}</TableCell>
                <TableCell>
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {(r.enabled_services || []).map((s) => (
                      <Chip key={s} label={SERVICE_LABELS[s] || s} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  {!r.is_active && (
                    <Button size="small" onClick={() => handleActivate(r)} sx={{ mr: 1 }}>
                      Активировать
                    </Button>
                  )}
                  <Tooltip title="Редактировать">
                    <IconButton size="small" onClick={() => openEdit(r)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Удалить">
                    <IconButton size="small" color="error" onClick={() => setDeleteTarget(r)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.id ? 'Редактирование компании' : 'Новая компания'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Название"
            margin="normal"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            fullWidth
            label="Главный домен"
            margin="normal"
            placeholder="tdcsm.ru"
            helperText="Головной сайт для legacy-текстов брендов и FAQ"
            value={form.main_site_host}
            onChange={(e) => setForm({ ...form, main_site_host: e.target.value })}
          />
          <FormControl sx={{ mt: 2 }}>
            <FormLabel>Тип ассортимента</FormLabel>
            <RadioGroup
              row
              value={form.assortment_mode}
              onChange={(e) => setForm({ ...form, assortment_mode: e.target.value as FormState['assortment_mode'] })}
            >
              <FormControlLabel value="categories" control={<Radio size="small" />} label="Категории" />
              <FormControlLabel value="branches" control={<Radio size="small" />} label="Ветки" />
              <FormControlLabel value="both" control={<Radio size="small" />} label="Оба" />
            </RadioGroup>
          </FormControl>
          <FormControl sx={{ mt: 1 }}>
            <FormLabel>Сервисы</FormLabel>
            <FormGroup>
              {Object.entries(SERVICE_LABELS).map(([key, label]) => (
                <FormControlLabel
                  key={key}
                  control={<Checkbox checked={form.enabled_services.includes(key)} onChange={() => toggleService(key)} />}
                  label={label}
                />
              ))}
            </FormGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Отмена</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={loading}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Удалить компанию?</DialogTitle>
        <DialogContent>{deleteTarget?.name}</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Отмена</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyManager;
